/**
 * Tool types matching backend ga_kasko_eda_be.models (ToolType, ToolModel, UpdateTool).
 * Field names kept in snake_case for API compatibility.
 */

// biome-ignore lint/style/useNamingConvention: Backend API uses snake_case
export type ToolType =
  | 'MfgDrillTool'
  | 'MfgSpotDrillTool'
  | 'MfgEndMillTool'
  | 'MfgThreadMillTool'
  | 'MfgTapTool'
  | 'MfgReamerTool'
  | 'MfgConicalMillTool';

/** All tool type values for filter dropdowns. */
export const TOOL_TYPE_VALUES: ToolType[] = [
  'MfgDrillTool',
  'MfgSpotDrillTool',
  'MfgEndMillTool',
  'MfgThreadMillTool',
  'MfgTapTool',
  'MfgReamerTool',
  'MfgConicalMillTool',
];

export interface HolderModel {
  id: string;
  holder_name: string;
  diameter_mm_1: number;
  diameter_mm_2: number;
  len_mm_1: number;
  len_mm_2: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface CreateHolder {
  holder_name?: string;
  diameter_mm_1: number;
  diameter_mm_2: number;
  len_mm_1: number;
  len_mm_2: number;
}

export interface UpdateHolder {
  holder_name?: string;
  diameter_mm_1?: number;
  diameter_mm_2?: number;
  len_mm_1?: number;
  len_mm_2?: number;
}

export interface ToolModel {
  id: string;
  holder_id: string;
  holder: HolderModel | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  tool_name: string;
  tool_type: ToolType;
  tool_comment: string;
  tool_number: number;
  /** Unique positive integer slot for NC/APT; set by backend, not user-editable via API. */
  tool_number_unique: number;
  /** User-assignable stack magazine slot; nullable, unique among non-null values. */
  stack_tool_number: number | null;
  tool_diameter: number;
  tool_corner_rad: number | null;
  tool_cut_angle: number | null;
  tool_overall_len: number;
  tool_cut_len: number | null;
  tool_len: number;
  tool_rotation: string;
  pitch: number;
  tool_pitch: number;
  length_1: number;
  thread_diameter: number;
  diameter: number;
  horizontal_distance: number | null;
  vertical_distance: number | null;
  tip_angle: number | null;
  flank_angle: number | null;
  spindle: number;
  feed_mach_value: number;
  support_finish: boolean;
  /** Thread depth (mm); backend uses -1 when not applicable. */
  thread_depth: number;
  /** Ball-end tool flag; null = unspecified. */
  ball_type: boolean | null;
  /** Diameter of the non-cutting (shank) part (mm); 0 when not applicable. */
  non_cutting_diameter: number;
}

/** Payload for creating a new tool. All fields shown in the dialog (nullable = user can leave empty). */
export interface CreateTool {
  // Basic identification
  tool_name: string;
  tool_type: ToolType;
  tool_comment: string;
  holder_id: string;
  // Core dimensions (required by backend)
  tool_number: number;
  tool_diameter: number;
  tool_overall_len: number;
  tool_len: number;
  non_cutting_diameter: number;
  // Optional dimensions (nullable on backend)
  tool_corner_rad: number | null;
  tool_cut_angle: number | null;
  tool_cut_len: number | null;
  // Rotation
  tool_rotation: string;
  // Thread / advanced geometry (backend defaults to -1.0 when not applicable)
  pitch: number;
  tool_pitch: number;
  length_1: number;
  thread_diameter: number;
  diameter: number;
  // Angle geometry (nullable on backend)
  horizontal_distance: number | null;
  vertical_distance: number | null;
  tip_angle: number | null;
  flank_angle: number | null;
  // Machine parameters
  spindle: number;
  feed_mach_value: number;
  support_finish: boolean;
  thread_depth: number;
  ball_type: boolean | null;
}

/** Update payload: only provided fields are sent to backend. */
export interface UpdateTool {
  tool_name?: string;
  tool_type?: ToolType;
  tool_comment?: string;
  tool_number?: number;
  tool_diameter?: number;
  tool_corner_rad?: number | null;
  tool_cut_angle?: number | null;
  tool_overall_len?: number;
  tool_cut_len?: number | null;
  tool_len?: number;
  tool_rotation?: string;
  pitch?: number;
  tool_pitch?: number;
  length_1?: number;
  thread_diameter?: number;
  diameter?: number;
  horizontal_distance?: number | null;
  vertical_distance?: number | null;
  tip_angle?: number | null;
  flank_angle?: number | null;
  spindle?: number;
  feed_mach_value?: number;
  support_finish?: boolean;
  holder_id?: string | null;
  thread_depth?: number;
  ball_type?: boolean | null;
  non_cutting_diameter?: number;
  stack_tool_number?: number | null;
}
